import { ImageOff } from "lucide-react"
import { useEffect, useMemo, useRef } from "react"
import { Skeleton } from "@/components/ui/skeleton"
import { useThumbnail } from "@/hooks/use-thumbnail"
import type { ReportTreeItemData } from "@/lib/report-tree"

interface ReportPreviewImageProps {
  node: ReportTreeItemData
}

/**
 * Displays a dynamically generated report thumbnail fetched from
 * ReportingHelper/GetThumbnailImage.
 */
export function ReportPreviewImage({ node }: Readonly<ReportPreviewImageProps>) {
  const thumbnailReportName = node.reportName ?? node.name
  const { data: blob, isLoading } = useThumbnail(thumbnailReportName)
  const previousUrlRef = useRef<string | null>(null)

  const objectUrl = useMemo(() => {
    if (!blob) return null
    return URL.createObjectURL(blob)
  }, [blob])

  useEffect(() => {
    if (previousUrlRef.current && previousUrlRef.current !== objectUrl) {
      URL.revokeObjectURL(previousUrlRef.current)
    }

    previousUrlRef.current = objectUrl

    return () => {
      if (previousUrlRef.current) {
        URL.revokeObjectURL(previousUrlRef.current)
        previousUrlRef.current = null
      }
    }
  }, [objectUrl])

  if (isLoading) {
    return (
      <Skeleton
        className="w-full rounded border"
        style={{ aspectRatio: "820 / 1060" }}
      />
    )
  }

  if (objectUrl) {
    return (
      <img
        src={objectUrl}
        alt={`Preview of ${node.name}`}
        className="w-full rounded border object-contain"
        style={{ aspectRatio: "820 / 1060" }}
      />
    )
  }

  return (
    <div
      className="flex w-full items-center justify-center rounded border bg-muted"
      style={{ aspectRatio: "820 / 1060" }}
    >
      <div className="flex flex-col items-center gap-2 text-muted-foreground">
        <ImageOff className="h-8 w-8" />
        <span className="text-sm">No preview available</span>
      </div>
    </div>
  )
}

/**
 * Lightweight fallback component when no node is selected or for non-file nodes.
 */
export function ReportPreviewImagePlaceholder() {
  return (
    <div
      className="flex w-full items-center justify-center rounded border bg-muted"
      style={{ aspectRatio: "820 / 1060" }}
    >
      <div className="flex flex-col items-center gap-2 text-muted-foreground">
        <ImageOff className="h-8 w-8" />
        <span className="text-sm">No preview available</span>
      </div>
    </div>
  )
}
